import { Box, Link, TextField, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { FiEdit } from "react-icons/fi";
import { toast } from "react-toastify";

import { useUpdateCalcPanel } from "../hooks/useFetchData.hook";

const URLNameInput = ({ elementData, onNameChange }: { elementData: any; onNameChange: (name: string) => void }) => {
    const nameRef = useRef<HTMLInputElement>(null);
    const [editingName, setEditingName] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const { updateCalcPanel, updateLoading } = useUpdateCalcPanel();

    useEffect(() => {
        if (nameRef.current) {
            if (elementData?.data?.name) {
                nameRef.current.value = elementData.data?.name;
            } else {
                nameRef.current.value = "";
            }
        }
    }, [elementData]);

    useEffect(() => {
        if (!updateLoading && success) {
            const timer = setTimeout(() => {
                setEditingName(false);
                setSuccess(false);
                setLoading(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [updateLoading, success]);

    const handleEditClickName = () => {
        setEditingName(true);
    };

    const handleCancelClick = () => {
        setEditingName(false);
    };

    const handleSaveClickName = async () => {
        const newValueName = nameRef?.current?.value;
        if (newValueName) {
            setLoading(true);
            setSuccess(false);
            setError(false);
            try {
                await updateCalcPanel({
                    url: `https://${elementData?.data?.url}`,
                    data: {
                        name: newValueName,
                    },
                });
            } catch (error) {
                setError(true);
                setLoading(false);
                toast.error("Error updating name");
                console.error(error);
            } finally {
                setSuccess(true);
                toast.success("Name updated successfully");
                setLoading(false);
                setTimeout(() => {
                    setEditingName(false);
                    setSuccess(false);
                    setLoading(false);
                }, 1000);
            }
        } else {
            setError(true);
            toast.error("Name is required");
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box display="flex" flexDirection="column" gap={1}>
                <Box className="elementForm" display="flex" alignItems="center" justifyContent="space-between">
                    <Typography component="label" htmlFor="editor">
                        Name
                    </Typography>
                    {elementData?.data?.activated &&
                        (editingName ? (
                            <Box className="saveCancel" display="flex" alignItems="center" gap={2}>
                                <Link component="button" onClick={handleCancelClick}>
                                    Cancel
                                </Link>
                                <Link component="button" onClick={handleSaveClickName}>
                                    {!success ? (
                                        error ? (
                                            <Typography color="error">Error</Typography>
                                        ) : loading || updateLoading ? (
                                            <Typography color="info">Saving...</Typography>
                                        ) : (
                                            <>Save</>
                                        )
                                    ) : (
                                        <Typography color="success">Saved</Typography>
                                    )}
                                </Link>
                            </Box>
                        ) : (
                            <Typography component="p" onClick={handleEditClickName} sx={{ cursor: "pointer" }}>
                                <FiEdit />
                            </Typography>
                        ))}
                </Box>
                <TextField
                    type="text"
                    inputRef={nameRef}
                    onChange={(e) => onNameChange(e.target.value)}
                    name="CName"
                    placeholder="Type a Name..."
                    defaultValue={editingName ? null : elementData?.data?.name || undefined}
                    InputProps={{
                        readOnly: elementData?.data?.activated && !editingName,
                    }}
                    fullWidth
                />
            </Box>
            <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body1" component="label" htmlFor="CUrl">
                    URL
                </Typography>
                <TextField
                    type="url"
                    name="CUrl"
                    placeholder="URL"
                    defaultValue={elementData?.data?.url}
                    InputProps={{
                        readOnly: true,
                    }}
                    fullWidth
                />
            </Box>
        </Box>
    );
};

export default URLNameInput;
